import { FC, useCallback, useMemo, useState } from 'react'
import classNames from 'classnames'
import { InView } from 'react-intersection-observer'
import { Button } from '@/atoms/Button'
import { FlexColumn, FlexRow } from '@/atoms/FlexContainers'
import { CaretRightIcon } from '@/atoms/Icons/CaretRightIcon'
import { LoadingSpinnerIcon } from '@/atoms/Icons/LoadingSpinnerIcon'
import { AsP, CaptionMD, CaptionSM, Display, EyebrowMD, EyebrowSM, TitleLG, TitleMD } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { formatPrice } from '@/services/PriceFormatter'
import { ShopifyProductWithBundles } from '@/services/Shopify/ShopifyService'
import { useShopifyCart } from '@/services/Shopify/hooks'
import { MoneyCurrencyCode } from '@/types/codegen-federation'
import { mergeUrlParams } from '@/utils/UriBuilder/UriBuilder'
import { useEcommerceEvents } from '@/utils/analytics/ecommerce-events'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { useShopifyUserAttributes } from '@/views/PreSales/hooks/useShopifyUserAttributes'
import useHandleUnexpectedError from '@/views/TicketCheckoutViews/hooks/useHandleUnexpectedError'
import { useGiveawayProductController } from '../../hooks/useGiveawayProductController'
import { useGiveawaySchedule } from '../../hooks/useGiveawaySchedule'
import { GiveawaySupportModal } from '../Modals/GiveawaySupportModal'

interface Props {
  tickets: ShopifyProductWithBundles[]
  theatricalSlug: string
  entriesProductId: string
}

const PriceDisplay: FC<{
  selectedProduct: ShopifyProductWithBundles | undefined
  isLowerPricedTickets: boolean
}> = ({ selectedProduct, isLowerPricedTickets }) => {
  const { t } = useTranslate('tickets')
  return (
    <>
      <EyebrowSM
        weight="semibold"
        color="white"
        className={classNames('mb-1 rounded-md px-2 py-1 bg-giveaway-primary', {
          visible: isLowerPricedTickets,
          invisible: !isLowerPricedTickets,
        })}
      >
        {t('tenPerTicket', '$10 per ticket')}
      </EyebrowSM>
      <FlexRow className="!items-top text-giveaway-text-primary-dark">
        <TitleMD weight="bold" className="mb-auto">
          $
        </TitleMD>
        <Display weight="semibold" className="items-top flex">
          {formatPrice((selectedProduct?.priceRange?.maxVariantPrice?.amount ?? 0) * 100, {
            currency: selectedProduct?.priceRange?.maxVariantPrice?.currencyCode ?? 'USD',
            ignoreEmptyDecimal: true,
            style: 'decimal',
          })}
        </Display>
      </FlexRow>
    </>
  )
}

const EntriesDisplay: FC<{
  isActive: boolean
  multiplier: number
  totalSelectedEntries: number
  dailyBonusEntries: number
  selectedTicketsQty: number
  minRewardTicketQuantity: number | undefined
}> = ({
  isActive,
  multiplier,
  totalSelectedEntries,
  dailyBonusEntries,
  selectedTicketsQty,
  minRewardTicketQuantity,
}) => {
  const { t } = useTranslate('tickets')

  if (!isActive || multiplier <= 0) return null

  return (
    <FlexColumn className="mt-2">
      <div className="flex flex-col items-center">
        <EyebrowSM
          weight="semibold"
          className="rounded-md bg-giveaway-primary px-2 pb-[5px] pt-1"
          color="giveaway-text-primary-light"
        >
          {t('totalGiveawayEntries', '{{totalSelectedEntries}} Giveaway Entries', { totalSelectedEntries })}
        </EyebrowSM>

        {dailyBonusEntries > 0 && selectedTicketsQty >= (minRewardTicketQuantity ?? 0) && (
          <CaptionMD weight="normal" color="giveaway-text-primary-dark" className="mt-2" italic>
            <Translate i18nKey="dailyBonusEntries" values={{ dailyBonusEntries }} t={t}>
              Includes{' '}
              <span className="font-bold text-giveaway-primary-accent">{{ dailyBonusEntries }} bonus entries</span>{' '}
              today only!
            </Translate>
          </CaptionMD>
        )}

        {!dailyBonusEntries && (
          <CaptionMD weight="normal" color="giveaway-text-primary-dark" className="mt-2" italic>
            <Translate i18nKey="bonusEntriesCardGiveawayV4" values={{ multiplier }} t={t}>
              Includes <span className="font-bold text-giveaway-primary-accent">{{ multiplier }}x bonus entries</span>{' '}
              this week only!
            </Translate>
          </CaptionMD>
        )}
      </div>
    </FlexColumn>
  )
}

const TicketQuantityButton: FC<{
  ticket: { id: string; quantity: number }
  isSelected: boolean
  onSelect: () => void
}> = ({ ticket, isSelected, onSelect }) => {
  const buttonClasses = classNames(
    '!text-sm !border-none h-[40px] grow-1 !w-full relative rounded-3xl',
    isSelected && '!bg-giveaway-primary-accent !text-white',
  )

  return (
    <li className="w-16 md:w-[70px]" key={ticket.id}>
      <Button onClick={onSelect} className={buttonClasses} variant="lightgray">
        {ticket.quantity}
      </Button>
    </li>
  )
}

export const PresaleChipSelectorCard: FC<Props> = ({ tickets, theatricalSlug, entriesProductId }) => {
  const [isLoading, setIsLoading] = useState(false)
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false)
  const { t } = useTranslate('tickets')
  const { trackProductAdded, trackProductViewed } = useEcommerceEvents()
  const { push } = useSafeRouter()
  const { handleUnexpectedError } = useHandleUnexpectedError({ reservationId: 'unknown' })
  const { createCart } = useShopifyCart()
  const { normal, isActive, minRewardTicketQuantity, dailyBonusEntries } = useGiveawaySchedule(theatricalSlug)
  const multiplier = normal?.multiplier ?? 1
  const { selectedTicketsQty, selectedProduct, ticketQuantityList, totalSelectedEntries, handleProductChange } =
    useGiveawayProductController(tickets, theatricalSlug)

  const attributes = useShopifyUserAttributes('theatrical-giveaway', 'theatrical giveaway ticket', theatricalSlug)

  const handleCheckout = useCallback(
    async (product: ShopifyProductWithBundles) => {
      const merchandiseId = product?.variants?.edges[0]?.node?.id

      if (!merchandiseId) {
        handleUnexpectedError({
          context: 'AddPreOrderTicketOption',
          error: new Error('Merchandise ID is not available for the selected product.'),
          customToastMessage: t('somethingWentWrongPleaseTryAgainPreOrder', 'Something went wrong, please try again.'),
        })
        return
      }

      const lines: Array<{ quantity: number; merchandiseId: string; sellingPlanId?: string }> = [
        { quantity: 1, merchandiseId: merchandiseId },
        {
          quantity: totalSelectedEntries,
          merchandiseId: `gid://shopify/ProductVariant/${entriesProductId}`,
        },
      ]

      try {
        setIsLoading(true)

        const { cart, errors } = await createCart({
          lines,
          attributes,
        })

        if (errors && errors.length > 0) {
          setIsSupportModalOpen(true)
          return
        }

        const checkoutUrl = mergeUrlParams(cart?.checkoutUrl, { utm_term: 'from-angel-com-flow' })

        if (checkoutUrl) {
          await trackProductAdded({
            category: 'theatrical preorder ticket',
            funnel: 'theatrical-pre-sale',
            sku: `preorder-${theatricalSlug}`,
            product_id: `preorder-${theatricalSlug}`,
            name: `${theatricalSlug} theatrical preorder ticket`,
            price: parseInt(product?.priceRange?.maxVariantPrice?.amount ?? 0),
            currency: (product?.priceRange?.maxVariantPrice?.currencyCode as MoneyCurrencyCode) ?? 'USD',
            quantity: 1,
            projectSlug: theatricalSlug,
          })
          return push(checkoutUrl)
        }
      } catch (error) {
        handleUnexpectedError({
          context: 'AddPreOrderTicketOption',
          error,
          customToastMessage: t('somethingWentWrongPleaseTryAgainPreOrder', 'Something went wrong, please try again.'),
        })
      } finally {
        setIsLoading(false)
      }
    },
    [
      attributes,
      createCart,
      entriesProductId,
      handleUnexpectedError,
      push,
      t,
      theatricalSlug,
      totalSelectedEntries,
      trackProductAdded,
    ],
  )

  const isLowerPricedTickets = useMemo(() => {
    return selectedTicketsQty >= 30
  }, [selectedTicketsQty])

  function handleInitialCheckoutIntent(): void {
    selectedProduct && handleCheckout(selectedProduct)
  }

  return (
    <InView
      triggerOnce
      onChange={(inView) => {
        if (!inView) return

        trackProductViewed({
          category: 'theatrical pre-order ticket',
          funnel: 'theatrical-pre-sale',
          sku: `pre-order-${theatricalSlug}`,
          product_id: `pre-order-${theatricalSlug}`,
          name: `${theatricalSlug} theatrical pre-order ticket`,
          price: Number((selectedProduct?.priceRange?.maxVariantPrice?.amount ?? 0) * 100),
          currency: 'USD',
          quantity: 1,
          projectSlug: theatricalSlug,
        })
      }}
    >
      <div className="flex flex-col">
        <div className="mx-auto flex w-full flex-col gap-6 rounded-3xl border-2 border-giveaway-primary-subtle bg-[#F5F5F5] px-8 py-6 md:bg-white">
          <div className="flex flex-col items-center gap-1">
            <PriceDisplay selectedProduct={selectedProduct ?? undefined} isLowerPricedTickets={isLowerPricedTickets} />
            <FlexColumn className="text-center">
              <TitleLG weight="semibold" color="giveaway-text-subtle-light">
                {t('fullPriceTicketsSelectedQty', '{{selectedQty}} Tickets', { selectedQty: selectedTicketsQty })}
              </TitleLG>
            </FlexColumn>
            <EntriesDisplay
              isActive={isActive}
              multiplier={multiplier}
              totalSelectedEntries={totalSelectedEntries}
              dailyBonusEntries={dailyBonusEntries}
              selectedTicketsQty={selectedTicketsQty}
              minRewardTicketQuantity={minRewardTicketQuantity}
            />
          </div>

          <div className="flex flex-col gap-4">
            <EyebrowMD as={AsP} weight="semibold" color="giveaway-text-subtle-light">
              {t('ticketQuantityPreSales', 'Ticket Quantity')}:
            </EyebrowMD>

            <ul className="flex grow-1 flex-wrap justify-center gap-3">
              {ticketQuantityList.map((ticket) => (
                <TicketQuantityButton
                  key={ticket.id}
                  ticket={ticket}
                  isSelected={ticket?.id === selectedProduct?.id}
                  onSelect={() => handleProductChange(ticket.id)}
                />
              ))}
            </ul>
          </div>

          {selectedProduct && (
            <Button
              variant="core-gray-900"
              size="lg"
              disabled={isLoading}
              onClick={() => handleInitialCheckoutIntent()}
              className="flex w-full items-center justify-between border-none !bg-giveaway-secondary !font-medium"
            >
              <span className="flex items-center gap-2">
                {t('next', 'Next')}
                {isLoading && <LoadingSpinnerIcon size={18} color="white" />}
              </span>
              <span className="gap flex items-center text-white/80">
                {formatPrice((selectedProduct?.priceRange?.maxVariantPrice?.amount ?? 0) * 100, {
                  currency: selectedProduct?.priceRange?.maxVariantPrice?.currencyCode ?? 'USD',
                  ignoreEmptyDecimal: true,
                })}
                <CaretRightIcon color="white" className="opacity-80" size={28} />
              </span>
            </Button>
          )}
        </div>
        <CaptionSM color="giveaway-text-subtle-light" className="mx-auto mt-6 max-w-[420px] px-4">
          <Translate t={t} i18nKey="presaleChipDisclaimerV4" values={{ theatricalSlug }}>
            Your ticket codes will be emailed after purchase. Redeem them at{' '}
            <a href={paths.tickets.checkout.showtimes(theatricalSlug)} target="_blank" className="underline">
              angel.com/tickets/{{ theatricalSlug }}
            </a>{' '}
            when showtimes are available. Questions? Check our FAQs!
          </Translate>
        </CaptionSM>
      </div>

      <GiveawaySupportModal
        open={isSupportModalOpen}
        onClose={() => {
          setIsSupportModalOpen(false)
        }}
      />
    </InView>
  )
}
